"use client";

import type React from "react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import { CustomCodeRenderer } from "./CustomCodeRenderer";
import {
	CUSTOM_CODE_ROOT_ATTR,
	type CustomCodeCssAudience,
} from "./customCodeCssScope";
import {
	executeHtmlCustomJs,
	normalizeStaticHtmlForEmbed,
	parseCompiledHtmlData,
} from "./customCodeHtmlRuntime";
import { useIsClient } from "./useIsClient";

type Props = {
	staticHtml: string;
	compiledCode: string;
	componentId: string;
	codeType: "html" | "react" | "nextjs";
	fallbackStyles?: React.CSSProperties;
	wrapperClassName?: string;
	wrapperId?: string;
	wrapperDataPbNode?: string;
	cssAudience?: CustomCodeCssAudience;
};

/**
 * SSR: emit captured static HTML (SEO + fast first paint).
 * Client: upgrade to live runtime (HTML-mode JS runs on the same SSR nodes).
 */
export function CustomCodeHybrid({
	staticHtml,
	compiledCode,
	componentId,
	codeType,
	fallbackStyles,
	wrapperClassName,
	wrapperId,
	wrapperDataPbNode,
	cssAudience = "builder",
}: Props) {
	const isClient = useIsClient();
	const [interactive, setInteractive] = useState(false);
	const staticRef = useRef<HTMLDivElement>(null);
	const htmlJsRanRef = useRef(false);
	const htmlJs = useMemo(() => {
		if (codeType !== "html") {
			return "";
		}
		const data = parseCompiledHtmlData(compiledCode);
		return data?.js?.trim() ?? "";
	}, [codeType, compiledCode]);
	const onReactInteractiveReady = useCallback(() => {
		setInteractive((prev) => (prev ? prev : true));
	}, []);
	const embedStaticHtml = useMemo(
		() => normalizeStaticHtmlForEmbed(staticHtml, componentId, cssAudience),
		[staticHtml, componentId, cssAudience]
	);

	// HTML hybrid: run tenant JS once on the SSR snapshot — no nested HtmlRenderer.
	// Production logs showed #185 right after dual HtmlRenderer layout effects +
	// setAttribute on the static ref (markHtmlHybridLive), not a render loop.
	useLayoutEffect(() => {
		if (codeType !== "html" || !isClient || htmlJsRanRef.current) {
			return;
		}
		const container = staticRef.current;
		if (!container) {
			return;
		}
		if (htmlJs) {
			try {
				executeHtmlCustomJs(htmlJs, container);
			} catch (e) {
				console.error("Error executing custom JS:", e);
			}
		}
		htmlJsRanRef.current = true;
	}, [codeType, htmlJs, isClient]);

	if (codeType === "html") {
		return (
			<div
				className={wrapperClassName}
				data-pb-node={wrapperDataPbNode}
				id={wrapperId}
				style={fallbackStyles}
				suppressHydrationWarning
				{...{ [CUSTOM_CODE_ROOT_ATTR]: componentId }}
			>
				<div
					dangerouslySetInnerHTML={{ __html: embedStaticHtml }}
					data-ssr="static"
					ref={staticRef}
					suppressHydrationWarning
				/>
			</div>
		);
	}

	const showStatic = !(isClient && interactive);

	return (
		<div
			className={wrapperClassName}
			data-pb-node={wrapperDataPbNode}
			id={wrapperId}
			style={fallbackStyles}
			suppressHydrationWarning
			{...{ [CUSTOM_CODE_ROOT_ATTR]: componentId }}
		>
			{showStatic ? (
				<div
					aria-hidden="true"
					dangerouslySetInnerHTML={{ __html: embedStaticHtml }}
					data-ssr="static"
					suppressHydrationWarning
				/>
			) : null}
			{isClient ? (
				<div
					style={
						interactive
							? undefined
							: {
									position: "absolute",
									width: 1,
									height: 1,
									overflow: "hidden",
									opacity: 0,
									pointerEvents: "none",
								}
					}
				>
					<CustomCodeRenderer
						codeType={codeType}
						compiledCode={compiledCode}
						componentId={componentId}
						cssAudience={cssAudience}
						nested
						onInteractiveReady={onReactInteractiveReady}
						skipClientGate
					/>
				</div>
			) : null}
		</div>
	);
}
